/**
 * Resolves which entity a screen is showing, from either URL shape.
 *
 * The port's own routes carry the id in the path (`/view/lead/19237971`), but
 * every link inside the legacy app still points at
 *
 *   EntityEdit.aspx?entityType=lead&entityID=U2FsdGVkX1…&EditLead=20268112
 *
 * so both are accepted. `entityID` is `jsFlowCommon.Encrypt(id)` — see
 * EntityEditDetails.js, which decrypts it on ready and sends the user to
 * AccessDenied.aspx when the result is not a positive number.
 */

import { setSessionOverrides } from 'api/client'
import { decryptQueryParams, flowDecrypt } from './crypto'
import type { EntityType } from 'types/entity'

export interface ResolvedEntityParams {
  entityType: EntityType
  entityId: string
  /** `entityID` was present but did not decrypt to a positive id (legacy: /AccessDenied.aspx). */
  accessDenied: boolean
  /** `EditLead` matched today's token, so the permission lookup is skipped. */
  skipPermissionLookup: boolean
}

/**
 * The `EditLead` token from EntityEdit.aspx.cs:
 *
 *   Year + Month + Day + (int)DayOfWeek, unpadded — e.g. 2026-08-11 (Tue) → "20268112"
 *
 * `DateTime.DayOfWeek` and `Date.getDay()` both number Sunday as 0.
 */
export function editLeadToken(now: Date = new Date()): string {
  return `${now.getFullYear()}${now.getMonth() + 1}${now.getDate()}${now.getDay()}`
}

/** Query lookup that ignores case, so `entityID`, `entityId` and `entityid` all match. */
function pick(params: Record<string, string>, ...names: string[]): string {
  const lowered: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) lowered[key.toLowerCase()] = value
  for (const name of names) {
    const value = lowered[name.toLowerCase()]
    if (value !== undefined && value !== '') return value
  }
  return ''
}

/**
 * Turns a raw `entityID` value into a plain id. Accepts an already-plain number
 * so `?entityId=19237971` keeps working alongside the encrypted form.
 */
function resolveEncryptedId(raw: string): string {
  if (!raw) return ''
  if (/^\d+$/.test(raw)) return raw

  const decrypted = flowDecrypt(raw).trim()
  // parseInt, then re-check: EntityEditDetails.js gates on `parseInt(...) > 0`.
  const id = Number.parseInt(decrypted, 10)
  return Number.isFinite(id) && id > 0 ? String(id) : ''
}

/**
 * Lifts session identity out of the (decrypted) query parameters and applies
 * it as the API session — so an encrypted link can carry UserId/ParentId etc.
 * and the app does not depend on `.env` for them. Recognised keys (any case):
 * UserId/uid, ParentId/pid, ModuleCode, TimeZone/strTimezone, Token.
 */
function applySessionFromParams(params: Record<string, string>): void {
  const userId = Number(pick(params, 'UserId', 'uid', 'hdnSipUserId'))
  const parentId = Number(pick(params, 'ParentId', 'ParentID', 'pid', 'hdnSipPid'))
  const moduleCode = pick(params, 'ModuleCode', 'hdnModuleCode')
  const timeZone = pick(params, 'TimeZone', 'strTimezone', 'hdnSipTimeZone')
  // ApiToken first: Kit19Sales sends the numeric services token under that name and
  // the SSO JWT as token=, and pick() is case-insensitive. A JWT is never the API
  // token — the services API rejects it — so a three-segment value is left alone.
  const rawToken = pick(params, 'ApiToken', 'hdnApiToken', 'Token')
  const token = rawToken.split('.').length === 3 ? '' : rawToken

  setSessionOverrides({
    ...(userId > 0 ? { userId } : {}),
    ...(parentId > 0 ? { parentId } : {}),
    ...(moduleCode ? { moduleCode } : {}),
    ...(timeZone ? { timeZone } : {}),
    ...(token ? { token } : {}),
  })
}

export function resolveEntityParams(
  search: string,
  routeParams: { entityType?: string; entityId?: string } = {},
  now: Date = new Date(),
): ResolvedEntityParams {
  // decryptQueryParams unwraps ?q=… and otherwise hands back the raw pairs.
  const params = decryptQueryParams(search)

  // The link may carry the session identity (UserId, ParentId, …) — apply it
  // before any data fetch runs.
  applySessionFromParams(params)

  const entityType = (routeParams.entityType || pick(params, 'entityType') || 'lead') as EntityType

  const editLead = pick(params, 'EditLead', 'EditEnquiry')
  const skipPermissionLookup = editLead !== '' && editLead === editLeadToken(now)

  if (routeParams.entityId) {
    return { entityType, entityId: routeParams.entityId, accessDenied: false, skipPermissionLookup }
  }

  const rawId = pick(params, 'entityID', 'entityId', 'id')
  const entityId = resolveEncryptedId(rawId)

  return {
    entityType,
    entityId,
    accessDenied: rawId !== '' && entityId === '',
    skipPermissionLookup,
  }
}
